import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Github, ExternalLink, Download } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import FileDownload from '@/components/ui/FileDownload';
import { projectsData } from '@/data';
import { toast } from '@/components/ui/use-toast';

// Helper to parse 'YYYY-MM-DD' as local date
function parseLocalDate(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any | null>(null);
  const [relatedProjects, setRelatedProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Fetch project data
    setLoading(true);
    
    // Find the project directly from projectsData
    const foundProject = projectsData.find(p => p.id === id);
    
    if (foundProject) {
      setProject(foundProject);
      
      // Get related projects
      const related = projectsData
        .filter(p => p.id !== id && p.category === foundProject.category)
        .slice(0, 3);
      
      setRelatedProjects(related);
    } else {
      // Show error toast if project not found
      toast({
        title: "Project not found",
        description: "The requested project could not be found.",
        variant: "destructive"
      });
      // Navigate back to projects page
      navigate('/projects');
    }
    
    setLoading(false);
    window.scrollTo(0, 0);
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading project details...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
        <p className="text-muted-foreground mb-6">The project you're looking for doesn't exist or has been removed.</p>
        <Button asChild>
          <Link to="/projects">Back to Projects</Link>
        </Button>
      </div>
    );
  }

  // Create an array of images from the project
  const images = project.images || [project.image];

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const longDescription = project.fullDescription || project.description;

  return (
    <>
      <Header />
      <motion.main 
        className="pt-24 pb-20"
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
      >
        <div className="container">
          <motion.div variants={fadeInVariants}>
            <Link 
              to="/projects" 
              className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Link>
          </motion.div>

          <motion.div variants={fadeInVariants}>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{project.title}</h1>
            {project.contextLabel && (
              <p className="text-sm text-muted-foreground mb-4">{project.contextLabel}</p>
            )}
            <div className="flex flex-wrap gap-2 mb-8">
              {project.tags.map((tag: string) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <motion.div variants={fadeInVariants} className="lg:col-span-2">
              <div className="glass-card overflow-hidden mb-10">
                <img 
                  src={images[currentImageIndex]} 
                  alt={project.title} 
                  className="w-full object-cover aspect-video"
                />
                
                {images.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {images.map((image: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`relative rounded-md overflow-hidden border-2 flex-shrink-0 w-20 h-20 transition-all ${
                          index === currentImageIndex ? 'border-primary' : 'border-transparent'
                        }`}
                      >
                        <img 
                          src={image} 
                          alt={`Preview ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <motion.div variants={fadeInVariants} className="glass-card p-6 mb-10">
                <h2 className="text-2xl font-semibold mb-6">Project Overview</h2>
                {longDescription.includes('<') ? (
                  <div
                    className="prose prose-lg dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: longDescription }}
                  />
                ) : (
                  <div className="prose prose-lg dark:prose-invert max-w-none whitespace-pre-wrap">
                    {longDescription}
                  </div>
                )}
              </motion.div>

              {project.challenges && project.outcome && String(project.challenges).trim() && String(project.outcome).trim() && (
                <motion.div variants={fadeInVariants} className="glass-card p-6 mb-10">
                  <h2 className="text-2xl font-semibold mb-6">Challenges & Outcomes</h2>
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-medium mb-3">Challenges</h3>
                    <p className="text-muted-foreground">{project.challenges}</p>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div>
                    <h3 className="text-xl font-medium mb-3">Outcome</h3>
                    <p className="text-muted-foreground">{project.outcome}</p>
                  </div>
                </motion.div>
              )}

              {project.files && project.files.length > 0 && (
                <motion.div variants={fadeInVariants} className="glass-card p-6">
                  <h2 className="text-2xl font-semibold mb-6">Project Files</h2>
                  <div className="space-y-4">
                    {project.files.map((file: any, index: number) => (
                      <FileDownload
                        key={index}
                        name={file.name}
                        path={file.path || file.url}
                        type={file.type}
                        size={file.size}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>

            <motion.div variants={fadeInVariants} className="space-y-6">
              <div className="glass-card p-6 sticky top-24">
                <h3 className="text-xl font-semibold mb-4">Project Details</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Project Type</h4>
                    <p>{project.category.charAt(0).toUpperCase() + project.category.slice(1)}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Completion Date</h4>
                    <p>{project.completionDate ? parseLocalDate(project.completionDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {project.githubUrl && (
                    <Button asChild variant="outline" className="w-full justify-start">
                      <a 
                        href={project.githubUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center"
                      >
                        <Github className="mr-2 h-4 w-4" />
                        View Source Code
                      </a>
                    </Button>
                  )}
                  
                  {project.liveUrl && (
                    <Button asChild variant="outline" className="w-full justify-start">
                      <a 
                        href={project.liveUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View Live Demo
                      </a>
                    </Button>
                  )}
                  
                  <Button asChild variant="default" className="w-full justify-start">
                    <a
                      href="/files/JenaroVegaBecerrilResumePublic2026.pdf"
                      download="JenaroVegaBecerrilResumePublic2026.pdf"
                      className="inline-flex items-center"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download resume
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.main>
      <Footer />
    </>
  );
};

export default ProjectDetail;
